import {
  COCKPIT_CONTROL_BASELINE_VERSION,
  COCKPIT_PUBLIC_ERROR_CODES,
  COCKPIT_SNAPSHOT_SCHEMA_VERSION,
  COCKPIT_USAGE_EXCLUSION_REASON_VALUES,
  COCKPIT_USAGE_REASON_VALUES,
  type ActivityCockpitSnapshot,
  type CockpitBaselineControl,
  type CockpitControlBaseline,
  type CockpitPublicError,
  type CockpitPublicErrorCode,
  type CockpitSection,
  type CockpitWorkPath,
  type ToCockpitSectionOptions,
} from './cockpit';
import { isInvalidCockpitSessionObservabilitySupplement } from './cockpit-session-observability';
import {
  isCockpitRevisionTrigger,
  isCockpitUsageObservationState,
} from './control-plane-usage-reliability';

const CONTROL_IDS: CockpitBaselineControl['id'][] = [
  'identity-and-work-path',
  'skill-traceability',
  'artifact-provenance',
  'applicable-tests-and-gates',
  'delivery-link',
  'telemetry-coverage',
  'reconciled-usage',
];

function baselineFor(
  workPath: CockpitWorkPath,
  authority: CockpitBaselineControl['authority'],
): CockpitControlBaseline {
  return Object.freeze({
    version: COCKPIT_CONTROL_BASELINE_VERSION,
    workPath,
    controls: Object.freeze(
      CONTROL_IDS.map(id => Object.freeze({ id, required: true, authority })),
    ),
  });
}

export const COCKPIT_CONTROL_BASELINES: Readonly<
  Record<CockpitWorkPath, CockpitControlBaseline>
> = Object.freeze({
  'control-plane': baselineFor('control-plane', 'observed'),
});

export function toCockpitSection<T>(
  value: T,
  options: ToCockpitSectionOptions = {},
): CockpitSection<T> {
  const empty = value === null || (Array.isArray(value) && value.length === 0);
  return {
    status: empty ? 'empty' : 'ready',
    value,
    confidence: options.confidence ?? 'confirmed',
    provenance: [...(options.provenance ?? [])],
    coverage: options.coverage ?? 'complete',
    updatedAt: options.updatedAt ?? null,
    limitations: [...(options.limitations ?? [])],
    error: null,
  };
}

export class CockpitContractError extends Error {
  constructor(
    readonly code: CockpitPublicErrorCode,
    message: string,
    readonly action: string | null = null,
  ) {
    super(message);
    this.name = 'CockpitContractError';
  }
}

export function toCockpitPublicError(error: unknown): CockpitPublicError {
  if (
    error instanceof CockpitContractError &&
    COCKPIT_PUBLIC_ERROR_CODES.includes(error.code)
  ) {
    return publicErrorCopy(error.code);
  }
  return {
    code: 'COCKPIT_COMPOSITION_FAILED',
    message: 'The requested cockpit section could not be loaded.',
    action: 'Retry the request or inspect the source availability.',
  };
}

const PUBLIC_ERROR_COPY: Record<
  CockpitPublicErrorCode,
  Pick<CockpitPublicError, 'message' | 'action'>
> = {
  COCKPIT_INVALID_QUERY: {
    message: 'The cockpit query is invalid.',
    action: 'Correct the query parameters and retry.',
  },
  AUTH_REQUIRED: {
    message: 'Authentication is required.',
    action: 'Sign in and retry the request.',
  },
  ACTIVITY_READ_FORBIDDEN: {
    message: 'You are not allowed to read this activity cockpit.',
    action: null,
  },
  ACTIVITY_NOT_FOUND: {
    message: 'The requested activity was not found.',
    action: null,
  },
  COCKPIT_REVISION_NOT_FOUND: {
    message: 'The requested cockpit revision was not found.',
    action: 'Open an available revision.',
  },
  ARTIFACT_CONTENT_NOT_FOUND: {
    message: 'Inspectable artifact content was not found.',
    action: null,
  },
  COCKPIT_WORK_PATH_MISMATCH: {
    message: 'The requested cockpit route does not own this activity.',
    action: 'Open the cockpit route for the activity work path.',
  },
  COCKPIT_REVISION_NOT_AVAILABLE: {
    message: 'Historical revisions are unavailable for an open activity.',
    action: null,
  },
  CONTROL_PLANE_HEALTH_INVALID: {
    message: 'The Control Plane health signal is inconsistent.',
    action: 'Correct the signal and retry.',
  },
  COCKPIT_COMPOSITION_FAILED: {
    message: 'The requested cockpit section could not be loaded.',
    action: 'Retry the request or inspect the source availability.',
  },
  COCKPIT_SNAPSHOT_SCHEMA_UNSUPPORTED: {
    message: 'The cockpit snapshot schema version is not supported.',
    action: 'Open a supported snapshot revision.',
  },
  COCKPIT_INTEGER_OUT_OF_RANGE: {
    message: 'The cockpit integer is outside the safe JSON contract.',
    action: null,
  },
};

function publicErrorCopy(code: CockpitPublicErrorCode): CockpitPublicError {
  return { code, ...PUBLIC_ERROR_COPY[code] };
}

export async function safeProjectSection<T>(
  projector: () => T | Promise<T>,
  options: ToCockpitSectionOptions = {},
): Promise<CockpitSection<T>> {
  try {
    return toCockpitSection(await projector(), options);
  } catch (error) {
    return {
      status: 'failed',
      value: null,
      confidence: 'load-failed',
      provenance: [...(options.provenance ?? [])],
      coverage: 'none',
      updatedAt: options.updatedAt ?? null,
      limitations: [...(options.limitations ?? [])],
      error: toCockpitPublicError(error),
    };
  }
}

export function decodeActivityCockpitSnapshot(
  input: unknown,
): ActivityCockpitSnapshot {
  const value = asRecord(input);
  if (value?.schemaVersion !== COCKPIT_SNAPSHOT_SCHEMA_VERSION) {
    throw new CockpitContractError(
      'COCKPIT_SNAPSHOT_SCHEMA_UNSUPPORTED',
      'The cockpit snapshot schema version is not supported.',
      'Open a supported snapshot revision.',
    );
  }
  const sections = asRecord(value.sections);
  if (
    typeof value.activityId !== 'string' ||
    value.workPath !== 'control-plane' ||
    typeof value.generatedAt !== 'string' ||
    typeof value.sourceCutoffAt !== 'string' ||
    typeof value.historical !== 'boolean' ||
    (value.revisionTrigger !== undefined &&
      !isCockpitRevisionTrigger(value.revisionTrigger)) ||
    !sections
  ) {
    throw new CockpitContractError(
      'COCKPIT_COMPOSITION_FAILED',
      'The cockpit snapshot payload is invalid.',
      'Rebuild the snapshot from its source evidence.',
    );
  }
  if (
    isInvalidCockpitSessionObservabilitySupplement(value.sessionObservability)
  ) {
    throw new CockpitContractError(
      'COCKPIT_COMPOSITION_FAILED',
      'The cockpit session observability payload is invalid.',
      'Rebuild the snapshot from its source evidence.',
    );
  }
  validateUsageDiagnostics(sections);
  validateCockpitRevision(value.revision);
  return value as unknown as ActivityCockpitSnapshot;
}

function validateCockpitRevision(revision: unknown): void {
  if (
    revision !== null &&
    (!Number.isSafeInteger(revision) || Number(revision) < 1)
  ) {
    throw new CockpitContractError(
      'COCKPIT_COMPOSITION_FAILED',
      'The cockpit snapshot revision is invalid.',
      'Open a valid snapshot revision.',
    );
  }
}

function validateUsageDiagnostics(sections: Record<string, unknown>): void {
  const usage = asRecord(sections.usage);
  const projection = asRecord(usage?.value);
  if (!projection || projection.diagnostics === undefined) return;

  const diagnostics = asRecord(projection.diagnostics);
  if (
    !diagnostics ||
    !isNonNegativeSafeInteger(diagnostics.observedEvents) ||
    !isNonNegativeSafeInteger(diagnostics.reconciledUsages) ||
    !isNonNegativeSafeInteger(diagnostics.unreconciledEvents) ||
    !isNonNegativeSafeInteger(diagnostics.excludedEvidence) ||
    !isUsageExclusionReasons(diagnostics.exclusionReasons) ||
    (diagnostics.lastObservedAt !== null &&
      typeof diagnostics.lastObservedAt !== 'string') ||
    !isUsageReason(diagnostics.inputReason) ||
    !isUsageReason(diagnostics.outputReason) ||
    (diagnostics.observationState !== undefined &&
      !isCockpitUsageObservationState(diagnostics.observationState))
  ) {
    throw new CockpitContractError(
      'COCKPIT_COMPOSITION_FAILED',
      'The cockpit usage diagnostics are invalid.',
      'Rebuild the snapshot from its source evidence.',
    );
  }
}

function isUsageExclusionReasons(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.every(
      reason =>
        typeof reason === 'string' &&
        COCKPIT_USAGE_EXCLUSION_REASON_VALUES.includes(
          reason as (typeof COCKPIT_USAGE_EXCLUSION_REASON_VALUES)[number],
        ),
    )
  );
}

function isNonNegativeSafeInteger(value: unknown): boolean {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}

function isUsageReason(value: unknown): boolean {
  return (
    value === null ||
    (typeof value === 'string' &&
      COCKPIT_USAGE_REASON_VALUES.includes(
        value as (typeof COCKPIT_USAGE_REASON_VALUES)[number],
      ))
  );
}

export function toSafeCockpitInteger(value: bigint | number | string): number {
  const integer = parseInteger(value);
  if (
    integer > BigInt(Number.MAX_SAFE_INTEGER) ||
    integer < BigInt(Number.MIN_SAFE_INTEGER)
  ) {
    throw integerRangeError();
  }
  return Number(integer);
}

function parseInteger(value: bigint | number | string): bigint {
  try {
    if (typeof value === 'number' && !Number.isSafeInteger(value)) {
      throw integerRangeError();
    }
    return BigInt(value);
  } catch {
    throw integerRangeError();
  }
}

function integerRangeError(): CockpitContractError {
  return new CockpitContractError(
    'COCKPIT_INTEGER_OUT_OF_RANGE',
    'The cockpit integer is outside the safe JSON contract.',
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}
